import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import { agentsApi, communicationsApi, modelRouterApi } from '../api/agentsApi';
import type {
  ExecuteAgentInput,
  ExecutePipelineInput,
  QueryExecutionsParams,
  UpdateAgentConfigInput,
} from '../types';

export const AGENT_KEYS = {
  all: ['agents'] as const,
  executions: (params: QueryExecutionsParams) => [...AGENT_KEYS.all, 'executions', params] as const,
  execution: (id: string, orgId: string) => [...AGENT_KEYS.all, 'execution', id, orgId] as const,
  config: (orgId: string) => [...AGENT_KEYS.all, 'config', orgId] as const,
  usage: (orgId: string) => [...AGENT_KEYS.all, 'usage', orgId] as const,
  planUsage: (orgId: string) => [...AGENT_KEYS.all, 'plan-usage', orgId] as const,
  queueStatus: () => [...AGENT_KEYS.all, 'queue-status'] as const,
  costs: (orgId: string, month?: number, year?: number) =>
    [...AGENT_KEYS.all, 'costs', orgId, month, year] as const,
  cacheStats: () => [...AGENT_KEYS.all, 'cache-stats'] as const,
  commsDashboard: (orgId: string) => [...AGENT_KEYS.all, 'comms-dashboard', orgId] as const,
  commsBudget: (orgId: string) => [...AGENT_KEYS.all, 'comms-budget', orgId] as const,
  leadStatus: (leadId: string, orgId: string) =>
    [...AGENT_KEYS.all, 'lead-status', leadId, orgId] as const,
};

export function useAgentExecutions(params: QueryExecutionsParams, enabled = true) {
  return useQuery({
    queryKey: AGENT_KEYS.executions(params),
    queryFn: () => agentsApi.listExecutions(params),
    enabled: !!params.organizationId && enabled,
    refetchInterval: 10000,
    staleTime: 10000,
  });
}

export function useAgentExecution(id: string | undefined, organizationId: string) {
  return useQuery({
    queryKey: AGENT_KEYS.execution(id!, organizationId),
    queryFn: () => agentsApi.getExecution(id!, organizationId),
    enabled: !!id && !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useAgentConfig(organizationId: string) {
  return useQuery({
    queryKey: AGENT_KEYS.config(organizationId),
    queryFn: () => agentsApi.getConfig(organizationId),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useAgentUsage(organizationId: string) {
  return useQuery({
    queryKey: AGENT_KEYS.usage(organizationId),
    queryFn: () => agentsApi.getUsage(organizationId),
    enabled: !!organizationId,
    refetchInterval: 30000,
    staleTime: 30000,
  });
}

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 404;
}

export function usePlanUsage(organizationId: string) {
  const query = useQuery({
    queryKey: AGENT_KEYS.planUsage(organizationId),
    queryFn: () => agentsApi.getPlanUsage(organizationId),
    enabled: !!organizationId,
    refetchInterval: 30000,
    staleTime: 30000,
    retry: (failureCount, error) => !isNotFound(error) && failureCount < 2,
  });
  return { ...query, isUnavailable: query.isError };
}

export function useQueueStatus() {
  return useQuery({
    queryKey: AGENT_KEYS.queueStatus(),
    queryFn: () => agentsApi.getQueueStatus(),
    refetchInterval: 5000,
    staleTime: 5000,
  });
}

export function useModelCosts(organizationId: string, month?: number, year?: number) {
  return useQuery({
    queryKey: AGENT_KEYS.costs(organizationId, month, year),
    queryFn: () => modelRouterApi.getCosts(organizationId, month, year),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useCacheStats() {
  return useQuery({
    queryKey: AGENT_KEYS.cacheStats(),
    queryFn: () => modelRouterApi.getCacheStats(),
    staleTime: 5 * 60_000,
  });
}

export function useCommsCostDashboard(organizationId: string) {
  return useQuery({
    queryKey: AGENT_KEYS.commsDashboard(organizationId),
    queryFn: () => communicationsApi.getCostDashboard(organizationId),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useCommsBudgetStatus(organizationId: string) {
  return useQuery({
    queryKey: AGENT_KEYS.commsBudget(organizationId),
    queryFn: () => communicationsApi.getBudgetStatus(organizationId),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useExecuteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ExecuteAgentInput) => agentsApi.execute(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...AGENT_KEYS.all, 'executions'],
      });
      queryClient.invalidateQueries({
        queryKey: AGENT_KEYS.usage(variables.organizationId),
      });
    },
  });
}

export function useCancelExecution() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({ id, organizationId }: { id: string; organizationId: string }) =>
      agentsApi.cancelExecution(id, organizationId),
    onSuccess: (_, variables) => {
      trackEvent(mp, 'agent.cancelled');
      queryClient.invalidateQueries({
        queryKey: AGENT_KEYS.execution(variables.id, variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: [...AGENT_KEYS.all, 'executions'],
      });
    },
  });
}

export function useUpdateAgentConfig() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: (input: UpdateAgentConfigInput) => agentsApi.updateConfig(input),
    onSuccess: (_, variables) => {
      trackEvent(mp, 'agent.config_updated');
      queryClient.invalidateQueries({
        queryKey: AGENT_KEYS.config(variables.organizationId),
      });
    },
  });
}

export function useCancelLeadPipeline() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: ({ leadId, organizationId }: { leadId: string; organizationId: string }) =>
      agentsApi.cancelLeadPipeline(leadId, organizationId),
    onSuccess: (_, variables) => {
      trackEvent(mp, 'agent.cancelled');
      queryClient.invalidateQueries({
        queryKey: AGENT_KEYS.leadStatus(variables.leadId, variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: [...AGENT_KEYS.all, 'executions'],
      });
    },
  });
}

export function useLeadAgentStatus(leadId: string, organizationId: string) {
  const query = useQuery({
    queryKey: AGENT_KEYS.leadStatus(leadId, organizationId),
    queryFn: () => agentsApi.getLeadAgentStatus(leadId, organizationId),
    enabled: !!leadId && !!organizationId,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.pipelineRunning ? 5000 : false;
    },
    staleTime: 5000,
  });

  return query;
}

export function useExecutePipeline() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: (input: ExecutePipelineInput) => agentsApi.executePipeline(input),
    onSuccess: (_, variables) => {
      trackEvent(mp, 'agent.pipeline_executed');
      queryClient.invalidateQueries({
        queryKey: [...AGENT_KEYS.all, 'lead-status'],
      });
      queryClient.invalidateQueries({
        queryKey: [...AGENT_KEYS.all, 'executions'],
      });
      queryClient.invalidateQueries({
        queryKey: AGENT_KEYS.usage(variables.organizationId),
      });
    },
  });
}
